import { ChangeDetectorRef, Component, HostBinding, OnDestroy } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import { IDataSource, IDataSourceItem } from '@se/cp.common';
import { IdDescriptionItem } from '@se/models';
import {
    EstensaBusWrapperService,
    EstensaReconiceService,
    EstensaSpeechModalService,
    EstensaWaitingService,
    IBusData,
    ListService,
    ModalDialogNotificationService,
    NotificationType,
    RadioSettingsService,
    UserConfigurationSettingsService
} from '@se/platform';
import {
    DataBinding, Guid, Logger,
    SeConstraintApplierService,
    SeConstraintConfiguratorService,
    SeConstraintNotifierService,
} from '@se/se.platform.base';
import {
    CpControlSuggestions,
    ExtendedSuggestion,
    MenuConfiguration,
    MenuEnum,
    SaveExtendedSuggestionEvent,
    Suggestion,
    User,
} from '@se/ui.text.editor';
import { FontInfo } from 'estensa-module';
import { flatten, isEmpty, isNil, uniq } from 'lodash-es';
import { BehaviorSubject, Observable, Subject, combineLatest, firstValueFrom } from 'rxjs';
import { filter, map, take, takeUntil, tap } from 'rxjs/operators';
import { CpEntity, CpTextEditorControl, PropertyType } from '../../cp-configuration/view-model';
import { CpDatasourceService } from '../../cp-datasource.service';
import { CpModelService } from '../../cp-model.service';
import { CpReportService } from '../../cp-report.service';
import { cpInitialize } from '../../store/cp.actions';
import { AbstractControlComponent } from '../abstract-control';
import {
    ClinicalDefaultReportRequest,
    ClinicalDefaultReportResponse,
    ClinicalDefaultReportService,
    SelectedExtendedSuggestionEvent,
} from './clinical-default-report.service';
import { TextEditorConfig } from './text-editor-config';

@Component({
    selector: 'cp-text-editor',
    templateUrl: './text-editor.component.html',
    styleUrls: ['./text-editor.component.scss'],
    standalone: false
})
export class CpTextEditorComponent extends AbstractControlComponent implements OnDestroy {
    @HostBinding('style.height')
    hostHeight = '100%';
    control!: CpTextEditorControl;
    isVisible$!: Observable<boolean>;
    editorConfig$!: Observable<TextEditorConfig>;
    private model: PropertyType | undefined | null;
    private clinicalDefaultReportRequest?: ClinicalDefaultReportRequest;
    lightLayout: boolean = true;
    suggestions$: BehaviorSubject<Suggestion[]> = new BehaviorSubject<Suggestion[]>([]);
    cpTextEditorSuggestionText: Map<string, CpControlSuggestions> = new Map<string, CpControlSuggestions>();
    private referringPhysician!: User;
    private internalDestroy$ = new Subject<void>();
    private isFromSpeechModal = false;

    constructor(
        readonly actions$: Actions,
        readonly clinicalDefaultReportService: ClinicalDefaultReportService,
        readonly dataSourceService: CpDatasourceService,
        readonly estensaBusWrapperService: EstensaBusWrapperService,
        private modelService: CpModelService,
        private estensaReconiceService: EstensaReconiceService,
        private settingsService: RadioSettingsService,
        readonly speechModalService: EstensaSpeechModalService,
        readonly reportService: CpReportService,
        cdr: ChangeDetectorRef,
        private userSettingService: UserConfigurationSettingsService,
        constraintNotifierService: SeConstraintNotifierService,
        private configuratorService: SeConstraintConfiguratorService,
        private waiterService: EstensaWaitingService,
        private constraintFilterService: SeConstraintApplierService,
        private modalDialogNotificationService: ModalDialogNotificationService,
        private translate: TranslateService,
        logger: Logger,
        private listService: ListService
    ) {
        super(cdr, constraintNotifierService, logger);
    }

    ngOnDestroy() {
        super.ngOnDestroy();
        if (this.clinicalDefaultReportRequest) {
            this.clinicalDefaultReportService.unregisterRequest(this.clinicalDefaultReportRequest);
        }
        this.clinicalDefaultReportService.unregisterCpControlSuggestion();
    }

    init$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(cpInitialize),
                takeUntil(this.destroyed$),
                tap(() => this.initializeToolbar()),
            ),
        { dispatch: false },
    );

    onFocus() {
        this.focusin.emit();
    }

    onModelChange(model: string) {
        const changed = this.control.onModelChange(this.modelService, model, !this.isDisabled);
        this.cdr.detectChanges();

        return changed;
    }

    onIsEmptyChange(isEmpty: boolean) {
        this.control.setIsEmpty(isEmpty);

        if (this.entity && this.entity.setPropertyErrorStatus) {
            this.entity.setPropertyErrorStatus(this.control.dataBinding.propertyName, 'isEmpty', true);
        }
        this.formControl?.updateValueAndValidity();
    }

    onVisibilityChange(isVisible: boolean) {
        const dataBinding = this.control.toggleDataBinding;
        if (!dataBinding) {
            return;
        }

        this.modelService.setValue(dataBinding, isVisible);
        this.edited.emit();
        this.formControl?.updateValueAndValidity();
    }

    onStartSpeechReconition(textNode: Array<string>) {
        let id: string = new Guid().toString();
        let speechText: string = textNode[0] ?? '';
        const regex = /<div[^>]*>(.*?)<\/div>/gs;
        const matches = [...speechText.matchAll(regex)];
        speechText = matches.map((match) => match[1]).join('');

        const fontInfo: FontInfo = {
            size: textNode[2]?.split('px')[0],
            family: textNode[1],
        };

        this.speechModalService.registerComponentId(id);
        this.speechModalService.openSpeechWindow(id, speechText, fontInfo);
        this.estensaReconiceService.setFont(fontInfo);
        this.speechModalService.setSpeechSkin().then(() => {
            this.configToolbarRefertatore();
            this.speechModalService.setThemeAndColors();
            this.speechModalService.getReconice().Dycendo.StartDictation();
        });
        this.changeTextOnCloseEvent(id);
    }

    configToolbarRefertatore() {
        let textEditorToolbarButtonsEnum = this.speechModalService.getReconice().TextEditorToolbarButtonsEnum;
        this.editorConfig$.subscribe((config: TextEditorConfig) => {
            let buttons: Array<string> = [];
            config.menu.forEach((configuration: MenuConfiguration, menu: MenuEnum) => {
                switch (menu) {
                    case MenuEnum.TEXT_ALIGNMENT:
                        if (!!configuration.visible) {
                            buttons.push(
                                textEditorToolbarButtonsEnum.ALIGN_LEFT,
                                textEditorToolbarButtonsEnum.ALIGN_RIGHT,
                                textEditorToolbarButtonsEnum.ALIGN_CENTER,
                                textEditorToolbarButtonsEnum.ALIGN_JUSTIFY,
                            );
                        }
                        break;
                    case MenuEnum.TEXT_FONT_WEIGHT:
                        if (!!configuration.visible) {
                            buttons.push(textEditorToolbarButtonsEnum.BOLD);
                        }
                        break;
                    case MenuEnum.TEXT_FONT_STYLE:
                        if (!!configuration.visible) {
                            buttons.push(textEditorToolbarButtonsEnum.ITALIC);
                        }
                        break;
                    case MenuEnum.TEXT_DECORATION:
                        if (!!configuration.visible) {
                            buttons.push(textEditorToolbarButtonsEnum.UNDERLINE);
                        }
                        break;
                    case MenuEnum.UNORDERED_LIST:
                        if (!!configuration.visible) {
                            buttons.push(textEditorToolbarButtonsEnum.BULLET_LIST);
                        }
                        break;
                    case MenuEnum.ORDERED_LIST:
                        if (!!configuration.visible) {
                            buttons.push(textEditorToolbarButtonsEnum.NUMBERED_LIST);
                        }
                        break;
                    case MenuEnum.NEW:
                    case MenuEnum.UNDO:
                    case MenuEnum.REDO:
                    case MenuEnum.SUGGESTIONS:
                    case MenuEnum.MICROPHONE:
                    default:
                        this.estensaReconiceService.setDefaultVisibleButton();
                        break;
                }
            });
            this.estensaReconiceService.setTextEditorButtonVisibility(uniq(buttons));
        });
    }

    onSelectedSuggestion(extendedSuggestion: ExtendedSuggestion) {
        if (!this.control.defaultText) {
            throw new Error(this.translate.instant("DefaultTextConfigurationIncomplete"));
        }
        this.clinicalDefaultReportService.selectSuggestion(
            extendedSuggestion,
            this.control.defaultText.defaultTextName,
        );
    }

    changeTextOnCloseEvent(speechId: string) {
        if (!this.isDisabled && this.canShowSpeechButton()) {
            this.estensaBusWrapperService.busDataFired
                .pipe(
                    filter((speechWindow: IBusData) => speechWindow.data?.id === speechId),
                    take(1),
                )
                .subscribe((busData: IBusData) => {
                    if (busData.messageType === 'ESTENSA_TEXT_TO_EDITOR') {
                        this.isFromSpeechModal = true;
                        this.onModelChange(busData.data.text);
                        this.updateFormControlValue(busData.data.text);
                    }
                });
        }
    }

    protected async init() {
        this.control.getEntityObservable(this.modelService).subscribe(this._entitySubject);

        this.formControl = this.makeControl();

        this.control
            .getModelObservable(this.modelService)
            .pipe(takeUntil(this.destroyed$))
            .subscribe((model) => {
                if (this.control.defaultTextEnabled) {
                    this.getControlSuggestionsInfo(model);
                    this.onChangeExaminations(model); //usato per il change su medico refertatore a runtime
                }
                if (isNil(model) || isEmpty(model)) {
                    return;
                }
                this.logger && this.logger.trace(`Model update ${this.control.name} ${model}`);
                this.updateFormControlValue(model);
            });
        this.isVisible$ = this.control.getVisibilityObservable(this.modelService);
        this.initializeToolbar();
    }

    private updateSuggestions() {
        this.clinicalDefaultReportService
            .getUpdateSuggestions(this.clinicalDefaultReportRequest!)
            .pipe(takeUntil(this.destroyed$), takeUntil(this.internalDestroy$))
            .subscribe((saveEvent) => {
                this.suggestions$.next(saveEvent.suggestions);
                this.waiterService.isWaiting = false;
            });
    }

    private onChangeExaminations(model: PropertyType | undefined | null) {
        const isPreview = this.modelService.getEntity(new DataBinding())?.getPropertyValue('IsPreview') ?? false;
        if (!isPreview) {
            this.reportService.referenceExamination$.subscribe({
                next: (exam) => {
                    const referringPhysician = exam.getPropertyValue('IdReferringPhysician') as CpEntity;
                    const idReferringPhysician = referringPhysician.getPropertyValue('Id') as string;
                    if (isNil(this.referringPhysician) || !isNil(this.referringPhysician) && this.referringPhysician.id !== idReferringPhysician) {
                        this.referringPhysician = {
                            id: idReferringPhysician,
                            isAuthorized: this.isUserAuthorized()
                        }
                        this.getControlSuggestionsInfo(model);
                        this.clinicalDefaultReportRequest = this.getClinicalDefaultReportRequest();
                        this.internalDestroy$.next();
                        this.retrieveSuggestions();
                        this.appendSuggestionToGroupTextEditors();
                        this.updateSuggestions();
                    }
                },
                error: (err) => {
                    throw new Error(`Cannot retrieve examinations ${err}`);
                }
            });
        } else {
            this.retrieveSuggestionsLight();
        }
    }

    private isUserAuthorized(): boolean {
        const authList = ['allDefaultReport_Get', 'allDefaultReport_Set', 'isReferring', 'isExecutor']
        return this.userSettingService.isAuthorized(authList, "&&");
    }

    private getControlSuggestionsInfo(model: PropertyType | undefined | null) {
        if (this.control.defaultTextEnabled) {
            this.model = model && !isNil(model) ? model : '';
            this.clinicalDefaultReportService.setCpControlSuggestion(
                this.control,
                this.model,
                this.referringPhysician,
            );
            this.cpTextEditorSuggestionText = this.clinicalDefaultReportService.getCpControlSuggestion();
        }
    }

    private getClinicalDefaultReportRequest(): ClinicalDefaultReportRequest | undefined {
        if (!this.control.defaultTextEnabled || !this.control.defaultText) {
            return undefined;
        }
        return {
            groupName: this.control.defaultText.defaultTextGroupName,
            idExaminations: this.reportService.examinationIds ?? [],
            idReferringPhysician: this.referringPhysician ? this.referringPhysician.id : '',
        };
    }

    private appendSuggestionToGroupTextEditors() {
        if (!this.control.defaultText) {
            throw new Error(this.translate.instant("DefaultTextConfigurationIncomplete"));
        }
        if (!this.clinicalDefaultReportRequest) return;
        this.clinicalDefaultReportService
            .getSelectedSuggestion(this.clinicalDefaultReportRequest, this.control.defaultText.defaultTextName)
            .pipe(takeUntil(this.destroyed$), takeUntil(this.internalDestroy$))
            .subscribe((event: SelectedExtendedSuggestionEvent) => {
                if (event.suggestion.policy) {
                    this.appendSuggestionToEntityContent(event.suggestion);
                } else {
                    this.replaceSuggestionToEntityContent(event.suggestion);
                }
            });
    }

    private appendSuggestionToEntityContent(extendedSuggestion: ExtendedSuggestion) {
        let currentValue = this.modelService.getValue(this.control.dataBinding);
        if (currentValue == null) {
            currentValue = '';
        }
        const newValue =
            currentValue + extendedSuggestion.suggestion.Sentence[this.control.defaultText?.defaultTextName ?? ''];
        this.onModelChange(newValue);
    }

    private replaceSuggestionToEntityContent(extendedSuggestion: ExtendedSuggestion) {
        const newValue = '' + extendedSuggestion.suggestion.Sentence[this.control.defaultText?.defaultTextName ?? ''];
        this.onModelChange(newValue);
    }

    private initializeToolbar() {
        this.clinicalDefaultReportRequest = this.getClinicalDefaultReportRequest();
        if (this.control.defaultTextEnabled && this.clinicalDefaultReportRequest && this.control.defaultText) {
            this.clinicalDefaultReportService.setCpSuggestionHashCode(
                this.clinicalDefaultReportService.getRequestHashCode(this.clinicalDefaultReportRequest),
                this.control.defaultText.defaultTextName,
            );
        }
        this.editorConfig$ = combineLatest({
            height: this.control.getHeightPxObservable(this.modelService),
            suggestions: this.suggestions$,
        }).pipe(
            map((heightAndSuggestions) => {
                return {
                    height: heightAndSuggestions.height,
                    isToggleEnabled: this.control.isToggleEnabled,
                    menu: this.control
                        .configureToolbar()
                        .set(MenuEnum.MICROPHONE, { visible: this.canShowSpeechButton() }),
                    suggestions: heightAndSuggestions.suggestions,
                    title: this.control.getTitle(),
                    fontFamily: this.control.fontFamily,
                    fontSize: this.control.fontSize,
                };
            }),
        );
        if (!this.control.defaultTextEnabled) {
            this.retrieveSuggestionsLight();
        }
    }

    private retrieveSuggestions(): void {
        const isPreview = this.modelService.getEntity(new DataBinding())?.getPropertyValue('IsPreview') ?? false;
        if (!isPreview) {
            if (this.control.defaultTextEnabled) {
                this.lightLayout = false;
                this.waiterService.isWaiting = true;
                this.clinicalDefaultReportService
                    .getSuggestions(this.clinicalDefaultReportRequest, this.control.name)
                    ?.pipe(takeUntil(this.destroyed$), takeUntil(this.internalDestroy$))
                    .subscribe({
                        next: (initialSuggestions: Suggestion[]) => {
                            this.suggestions$.next(initialSuggestions);
                            this.waiterService.isWaiting = false;
                        },
                        error: (err) => {
                            this.errorLoadSuggestions();
                            this.suggestions$.next([]);
                        }
                    });
            }
        } else if (isPreview && this.control.suggestions && !this.control.defaultTextEnabled) {
            this.lightLayout = true;
            this.getSuggestionsFromConfiguration();
        }
    }

    private retrieveSuggestionsLight() {
        const isPreview = this.modelService.getEntity(new DataBinding())?.getPropertyValue('IsPreview') ?? false;
        if (!isPreview) {
            if (this.control.suggestions && !this.control.defaultTextEnabled) {
                this.lightLayout = true;
                this.getSuggestionsFromConfiguration();
            }
        } else if (isPreview && this.control.suggestions && !this.control.defaultTextEnabled) {
            this.lightLayout = true;
            this.getSuggestionsFromConfiguration();
        }
    }

    private getSuggestionsFromConfiguration() {
        const initialSuggestions = this.initializeSuggestionsFromDataSource();
        this.suggestions$.next(initialSuggestions);
        if (!isNil(this.control.suggestions)) {
            const constraints = uniq(
                flatten(
                    this.configuratorService.constraintSources
                        .getSourcesByListName(this.control.suggestions)
                        .map((s) => s.constraints),
                ),
            );
            const dataBindingContext = 'dataBinding' in this.control ? this.control.dataBinding.toString() : undefined;

            for (const c of constraints) {
                this.constraintNotifier
                    .register(c.id, dataBindingContext)
                    .pipe(takeUntil(this.destroyed$))
                    .subscribe((_) => this.handleFilterNotification(c.id));
            }
        }
    }

    private initializeSuggestionsFromDataSource(): Suggestion[] {
        const suggestionByService: IDataSource =
            (this.control.suggestions ? this.dataSourceService.getById(this.control.suggestions) : undefined) ?? [];
        return this.transformDataSourceToSuggestions(suggestionByService);
    }

    protected handleFilterNotification(constraintId: string) {
        this.entity?.select();
        const filteredItems = this.constraintFilterService.applyFilter(constraintId) as IDataSource;
        const filteredSuggestions = this.transformDataSourceToSuggestions(filteredItems);
        this.suggestions$.next(filteredSuggestions);
    }

    private transformDataSourceToSuggestions(dataSource: IDataSource): Array<Suggestion> {
        let idCount: number = 0;
        return dataSource.map((item: IDataSourceItem) => {
            let suggestion: Suggestion = {
                id: idCount++,
                guid: item.Id,
                Description: item.Description,
                Sentence: { text: item['Sentence' as keyof IDataSourceItem] as string },
                IsNormality: !!item['IsNormality' as keyof IDataSourceItem],
                code: item['code' as keyof IDataSourceItem] as string,
                newLineAfter: false,
            };
            return suggestion;
        });
    }

    private canShowSpeechButton(): boolean {
        return (
            !!this.settingsService.radioSettings?.enableSpeechRecognition &&
            this.estensaReconiceService.isRecomedConnectedAndLoggedOn() &&
            this.userSettingService.isAuthorized(['isReferring'], "single")
        );
    }

    private makeControl() {
        let validators = [
            // Non aggiungere il required, se ne occupa il validatore custom.
            this.makeValidator(this._entitySubject, this.control.dataBinding.propertyName, false),
            ...this.control.getValidators(this.modelService),
        ];
        return new FormControl(this.control.getInitialValue(this.modelService), validators);
    }

    private updateFormControlValue(model: PropertyType | undefined) {
        if (!this.formControl) {
            return;
        }
        const formControlValue = this.formControl.value;
        // Non aggiornare il campo se i valori sono uguali.
        if (!model && !model === !formControlValue) {
            return;
        }
        this.formControl.setValue(model);
    }

    onUpdateSuggestion(event: SaveExtendedSuggestionEvent) {
        if (this.clinicalDefaultReportRequest && this.control.defaultText) {
            if (!event.toDelete) {
                this.updateAndSaveSuggestions(event);
            } else {
                this.deleteSuggestions(event);
            }
        }
    }

    private async updateAndSaveSuggestions(event: SaveExtendedSuggestionEvent) {
        this.waiterService.isWaiting = true;
        let request: ClinicalDefaultReportResponse;
        if (event.isNew) {
            request = await this.requestToSave(event.suggestions[event.index]);
        } else {
            request = await this.requestToSave(event.suggestions[event.index]);
            request.id = event.suggestions[event.index].guid;
        }
        this.clinicalDefaultReportService
            .saveSuggestion(request)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (res: ClinicalDefaultReportResponse) => {
                    this.logger?.info('Suggestion save with success');
                    if (event.isNew) {
                        event.suggestions[event.index].guid = res.id;
                        event.suggestions[event.index].referringPhysician = res.referringPhysician;
                        if (isNil(event.groupNameHashCode)) {
                            const hashCode = this.clinicalDefaultReportService.getRequestHashCode(this.clinicalDefaultReportRequest);
                            event.groupNameHashCode = hashCode;
                        }
                    }
                    this.messageConfirmAction();
                    this.clinicalDefaultReportService.updateSuggestions(event);
                },
                error: (err) => {
                    this.logger?.error(`Suggestion save failed ${err.message}`);
                    this.errorGenericMessage();
                    this.waiterService.isWaiting = false;
                },
                complete: () => {
                    this.waiterService.isWaiting = false;
                    this.logger?.info('Suggestion save complete');
                },
            });
    }

    private deleteSuggestions(event: SaveExtendedSuggestionEvent) {
        const idClinicalDefaultReport = event.suggestions[event.index].guid ?? '';
        this.waiterService.isWaiting = true;
        this.clinicalDefaultReportService
            .deleteSuggestion([idClinicalDefaultReport])
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (_res) => {
                    event.suggestions.splice(event.index, 1);
                    this.clinicalDefaultReportService.updateSuggestions(event);
                    this.messageConfirmAction();
                },
                error: (err) => {
                    this.logger?.error(`Suggestion delete failed ${err.message}`);
                    this.errorGenericMessage();
                    this.waiterService.isWaiting = false;
                },
                complete: () => {
                    this.waiterService.isWaiting = false;
                    this.logger?.info('Suggestion delete complete');
                },
            });
    }

    private async requestToSave(event: Suggestion): Promise<ClinicalDefaultReportResponse> {
        const _modules = await this.modules();
        const modules = event.modules?.length ? event.modules : _modules;
        const modalities = event.modalities?.length ? event.modalities : this.modalities();
        let paramSave: ClinicalDefaultReportResponse = {
            code: event.code ?? '',
            description: event.Description,
            referringPhysician: { id: event.referringPhysician?.id },
            texts: event.Sentence,
            modalities: modules.length ? [] : modalities,
            standardModalities: event.standardModalities?.length ? event.standardModalities : [],
            groupName: event.groupName ? event.groupName : this.control.defaultText?.defaultTextGroupName,
            modules: modules.length ? modules : [],
            standardModules: event.standardModules ? event.standardModules : []
        };
        return paramSave;
    }

    private modalities(): Array<IdDescriptionItem> {
        const exam = this.reportService.referenceExamination?.getPropertyValue('IdExam') as CpEntity;
        const idModality = exam.getPropertyValue('IdModality') as string;
        const modality: IdDescriptionItem = { id: idModality, description: "" };
        return [modality];
    }

    private codeModules() {
        //qui ci sono i Code della source
        return firstValueFrom(this.reportService.modules$);
    }

    private async modules() {
        const modulesCode = await this.codeModules();
        const modules: IDataSourceItem[] = await firstValueFrom(this.listService.getList('lsModule'));
        return modules.filter(mod => modulesCode.includes(mod.code)).map(mod => ({
            id: mod.guid,
            description: mod.code
        }));

    }

    private messageConfirmAction() {
        this.modalDialogNotificationService
            .notify({
                message: this.translate.instant('SuccessOperation'),
                title: this.translate.instant('Warning'),
                type: NotificationType.information,
                buttonYes: false,
                buttonNo: false,
                buttonOk: true,
                buttonCancel: false,
            });
    }

    private errorGenericMessage() {
        this.modalDialogNotificationService
            .notify({
                message: this.translate.instant('GenericExecutionFailure'),
                title: this.translate.instant('Warning'),
                type: NotificationType.error,
                buttonYes: false,
                buttonNo: false,
                buttonOk: true,
                buttonCancel: false,
            });
    }

    private errorLoadSuggestions() {
        this.modalDialogNotificationService
            .notify({
                message: this.translate.instant('CannotLoadPredefinite'),
                title: this.translate.instant('Warning'),
                type: NotificationType.error,
                buttonYes: false,
                buttonNo: false,
                buttonOk: true,
                buttonCancel: false,
            });
    }
}
